<template>
  <div class="denounced-users-report">
    <!-- Layout com filtros na direita e relatório na esquerda -->
    <b-row class="h-100 no-gutters">
      <!-- Coluna da esquerda - Relatório -->
      <b-col cols="9" class="h-100">
        <b-card v-if="isMasterUser" id="report" no-body class="h-100">
          <div class="report-print-root" :class="printRootClass">
            <ReportPrintHeader
              v-if="printing"
              :title="printTitle"
              :subtitle="`Relatório de usuários denunciados · Total: ${totalFilteredCount}`"
              :logo="printLogo"
              :generated-at="formatPrintDateTime(printGeneratedAt)"
              :company="printCompany"
              :period="printPeriod"
              :emitter="printEmitter"
            />
            <b-card-body class="position-relative">
              <div
                class="d-flex justify-content-between align-items-center mb-3 report-screen-heading"
              >
                <h2>{{ reportTitle }}</h2>
                <div class="d-flex align-items-center">
                  <b-badge variant="info" pill class="p-2 mr-2">
                    <i class="fas fa-file-alt mr-1"></i>
                    Total: {{ totalFilteredCount }}
                  </b-badge>
                  <b-button
                    v-if="!printing"
                    variant="primary"
                    class="add-denounced-btn"
                    @click="openAddDenouncedModal"
                  >
                    <i class="fas fa-plus-circle"></i> Vincular Denunciado
                  </b-button>
                </div>
              </div>

              <div class="complaints-layout-surface">
                <div v-if="loadingReport" class="skeleton-overlay">
                  <div class="table-responsive">
                    <table class="table complaints-layout-table skeleton-table">
                      <thead class="complaints-layout-header">
                        <tr>
                          <th v-for="field in activeFields" :key="field.key">
                            {{ field.label }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="i in 8" :key="i">
                          <td v-for="field in activeFields" :key="field.key">
                            <b-skeleton
                              :width="
                                field.key === 'complaintCount' ? '60px' : '90%'
                              "
                            ></b-skeleton>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div
                  v-if="!loadingReport && reportItems.length === 0"
                  class="complaints-layout-empty text-center"
                >
                  <p>Nenhum usuário denunciado encontrado.</p>
                </div>

                <b-table
                  v-show="!loadingReport && reportItems.length > 0"
                  borderless
                  hover
                  :responsive="!printing"
                  :items="reportItems"
                  :fields="printFields"
                  :filter="currentFilter"
                  :filter-function="filterResults"
                  table-class="complaints-layout-table"
                  thead-class="complaints-layout-header"
                  tbody-tr-class="complaints-layout-row"
                  @filtered="onFiltered"
                >
                  <template #cell(relationshipType)="data">
                    <div class="relationship-type">
                      <b-badge
                        v-if="data.value"
                        :variant="
                          data.value === 'internal' ? 'primary' : 'info'
                        "
                      >
                        {{
                          data.value === "internal"
                            ? "Funcionário Interno"
                            : "Terceiro"
                        }}
                      </b-badge>
                      <span v-else class="text-muted">-</span>
                    </div>
                  </template>
                  <template #cell(name)="data">
                    <div class="name-cell">
                      {{ data.value || data.item.name || "" }}
                    </div>
                  </template>
                  <template #cell(companyName)="data">
                    {{ data.value }}
                  </template>
                  <template #cell(complaintCount)="data">
                    <div
                      v-if="!printing"
                      class="complaint-count-simple clickable"
                      @click="showComplaintsModal(data.item)"
                    >
                      {{ data.value }}
                    </div>
                    <span v-else>{{ data.value }}</span>
                  </template>
                </b-table>
              </div>
            </b-card-body>
          </div>
        </b-card>

        <b-card v-else id="report" class="h-100">
          <div class="report-print-root" :class="printRootClass">
            <ReportPrintHeader
              v-if="printing"
              :title="printTitle"
              :subtitle="`Relatório de usuários denunciados · Total: ${totalFilteredCount}`"
              :logo="printLogo"
              :generated-at="formatPrintDateTime(printGeneratedAt)"
              :company="printCompany"
              :period="printPeriod"
              :emitter="printEmitter"
            />
            <div
              class="d-flex justify-content-between align-items-center mb-3 report-screen-heading"
            >
              <h2>{{ reportTitle }}</h2>
              <div class="d-flex align-items-center">
                <b-badge variant="info" pill class="p-2 mr-2">
                  <i class="fas fa-file-alt mr-1"></i>
                  Total: {{ totalFilteredCount }}
                </b-badge>
                <b-button
                  v-if="!printing"
                  variant="primary"
                  class="add-denounced-btn"
                  @click="openAddDenouncedModal"
                >
                  <i class="fas fa-plus-circle"></i> Vincular Denunciado
                </b-button>
              </div>
            </div>
            <b-card-body class="position-relative">
              <div class="complaints-layout-surface">
                <div v-if="loadingReport" class="skeleton-overlay">
                  <div class="table-responsive">
                    <table class="table complaints-layout-table skeleton-table">
                      <thead class="complaints-layout-header">
                        <tr>
                          <th v-for="field in activeFields" :key="field.key">
                            {{ field.label }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="i in 8" :key="i">
                          <td v-for="field in activeFields" :key="field.key">
                            <b-skeleton
                              :width="
                                field.key === 'complaintCount' ? '60px' : '90%'
                              "
                            ></b-skeleton>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div
                  v-if="!loadingReport && reportItems.length === 0"
                  class="complaints-layout-empty text-center"
                >
                  <p>Nenhum usuário denunciado encontrado.</p>
                </div>

                <b-table
                  v-show="!loadingReport && reportItems.length > 0"
                  borderless
                  hover
                  :responsive="!printing"
                  :filter="currentFilter"
                  :filter-function="filterResults"
                  :items="reportItems"
                  :fields="printFields"
                  class="force-printing-overflow"
                  table-class="complaints-layout-table"
                  thead-class="complaints-layout-header"
                  tbody-tr-class="complaints-layout-row"
                  @filtered="onFiltered"
                >
                  <template #cell(relationshipType)="data">
                    <div class="relationship-type">
                      <b-badge
                        v-if="data.value"
                        :variant="
                          data.value === 'internal' ? 'primary' : 'info'
                        "
                      >
                        {{
                          data.value === "internal"
                            ? "Funcionário Interno"
                            : "Terceiro"
                        }}
                      </b-badge>
                      <span v-else class="text-muted">-</span>
                    </div>
                  </template>
                  <template #cell(name)="data">
                    <div class="name-cell">
                      {{ data.value || data.item.name || "" }}
                    </div>
                  </template>
                  <template #cell(companyName)="data">
                    {{ data.value }}
                  </template>
                  <template #cell(complaintCount)="data">
                    <div
                      v-if="!printing"
                      class="complaint-count-simple clickable"
                      @click="showComplaintsModal(data.item)"
                    >
                      {{ data.value }}
                    </div>
                    <span v-else>{{ data.value }}</span>
                  </template>
                </b-table>
              </div>
            </b-card-body>
          </div>
        </b-card>
      </b-col>
      <!-- Coluna da direita - Filtros -->
      <b-col cols="3" class="pl-3">
        <b-card header="Parâmetros" class="filters-sidebar h-100">
          <div class="filters-container">
            <!-- Filtro de Empresa (apenas para root ou company-root gestora) -->
            <div v-if="showCompanyFilter" class="filter-group">
              <div class="filter-header" @click="toggleSection('empresa')">
                <h6 class="filter-title">Empresa</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.empresa
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse
                id="collapse-empresa"
                :visible="!collapsedSections.empresa"
              >
                <b-form-group label="Selecionar Empresa" class="mb-3">
                  <b-form-select
                    v-model="filter.selectedCompany"
                    :options="companyOptions"
                    @change="onFilterChange"
                  />
                </b-form-group>
              </b-collapse>
            </div>

            <!-- Filtro de Tipo de Relacionamento -->
            <div class="filter-group">
              <div
                class="filter-header"
                @click="toggleSection('relacionamento')"
              >
                <h6 class="filter-title">Tipo de Relacionamento</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.relacionamento
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse
                id="collapse-relacionamento"
                :visible="!collapsedSections.relacionamento"
              >
                <b-form-checkbox-group
                  v-model="filter.relationshipType"
                  :options="relationshipTypeOpts"
                  stacked
                  class="mb-3"
                  @change="onFilterChange"
                />
              </b-collapse>
            </div>

            <!-- Filtro de Número Mínimo de Denúncias -->
            <div class="filter-group">
              <div class="filter-header" @click="toggleSection('denuncias')">
                <h6 class="filter-title">Número de Denúncias</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.denuncias
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse
                id="collapse-denuncias"
                :visible="!collapsedSections.denuncias"
              >
                <b-form-group label="Mínimo de denúncias" class="mb-3">
                  <b-form-input
                    v-model="filter.minComplaints"
                    type="number"
                    min="1"
                    placeholder="Ex: 2"
                    @input="onFilterChange"
                  />
                </b-form-group>
              </b-collapse>
            </div>

            <!-- Filtros de Classificação -->
            <div v-if="!isMasterUser" class="filter-group">
              <div
                class="filter-header"
                @click="toggleSection('classificacoes')"
              >
                <h6 class="filter-title">Classificações</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.classificacoes
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse
                id="collapse-classificacoes"
                :visible="!collapsedSections.classificacoes"
              >
                <b-form-group
                  v-if="companyClassification.receivingMeans.enabled"
                  label="Meio de Recebimento"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.receivingMeans"
                    :options="getClassificationOpts('receivingMeans')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="companyClassification.origin.enabled"
                  label="Origem"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.origin"
                    :options="getClassificationOpts('origin')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="companyClassification.reportingAgent.enabled"
                  label="Agente Denunciante"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.reportingAgent"
                    :options="getClassificationOpts('reportingAgent')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="companyClassification.level.enabled"
                  label="Classificação/Classe Interna"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.level"
                    :options="getClassificationOpts('level')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="companyClassification.nature.enabled"
                  label="Natureza"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.nature"
                    :options="getClassificationOpts('nature')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="companyClassification.responsibleDepartment.enabled"
                  label="Setor Responsável"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.responsibleDepartment"
                    :options="getClassificationOpts('responsibleDepartment')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="companyClassification.involvedDepartment.enabled"
                  label="Setor da Ocorrência"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.involvedDepartment"
                    :options="getClassificationOpts('involvedDepartment')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="companyClassification.businessUnit.enabled"
                  label="Unidade"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.businessUnit"
                    :options="getClassificationOpts('businessUnit')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="companyClassification.businessGroup.enabled"
                  label="Empresa do Grupo"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.businessGroup"
                    :options="getClassificationOpts('businessGroup')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="companyClassification.relatedProject.enabled"
                  label="Contrato/Projeto"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.relatedProject"
                    :options="getClassificationOpts('relatedProject')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="companyClassification.complaintClosure.enabled"
                  label="Encerramento"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.complaintClosure"
                    :options="getClassificationOpts('complaintClosure')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>
              </b-collapse>
            </div>

            <!-- Filtros de Comitê -->
            <div v-if="!isMasterUser" class="filter-group">
              <div class="filter-header" @click="toggleSection('comite')">
                <h6 class="filter-title">Comitê</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.comite
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse
                id="collapse-comite"
                :visible="!collapsedSections.comite"
              >
                <b-form-group label="Relator" class="mb-2">
                  <b-form-radio-group
                    v-model="filter.committee.admin"
                    :options="[
                      { value: null, text: 'Todos' },
                      ...companyUsersList,
                    ]"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group label="Membros" class="mb-2">
                  <b-form-checkbox-group
                    v-model="filter.committee.members"
                    :options="companyUsersList"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>
              </b-collapse>
            </div>

            <!-- Botões de ação -->
            <div class="d-flex flex-column mt-3">
              <b-button
                variant="secondary"
                class="mb-2"
                @click="clearParameters"
              >
                <i class="fas fa-trash"></i> Limpar
              </b-button>
              <b-button variant="primary" @click="print">
                <i class="fas fa-print"></i> Imprimir
              </b-button>
              <b-button variant="success" class="mt-2" @click="exportCSV">
                <i class="fas fa-file-csv"></i> Exportar CSV
              </b-button>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Modal com detalhes das denúncias -->
    <b-modal
      v-model="showModal"
      title="Detalhes das Denúncias"
      size="xl"
      scrollable
      centered
      hide-footer
    >
      <div v-if="selectedDenounced && selectedDenounced.complaints">
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h5 class="mb-0">Denúncias de {{ selectedDenounced.name }}</h5>
          <b-button
            variant="success"
            class="add-complaint-btn"
            @click="openAddComplaintModal"
          >
            <i class="fas fa-plus-circle"></i> Adicionar Denúncia
          </b-button>
        </div>
        <div class="denounced-info mb-4">
          <h6 class="text-muted mb-2">Informações do Denunciado</h6>
          <div class="row">
            <div class="col-md-6">
              <strong>Nome:</strong> {{ selectedDenounced.name }}
            </div>
            <div class="col-md-6">
              <strong>Cargo:</strong> {{ selectedDenounced.office }}
            </div>
            <div class="col-md-6">
              <strong>Departamento:</strong> {{ selectedDenounced.department }}
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="mb-0">
            Denúncias ({{ selectedDenounced.complaints.length }})
          </h6>
          <div v-if="refreshingComplaints" class="text-muted">
            <b-spinner small></b-spinner>
            <span class="ml-2">Atualizando...</span>
          </div>
        </div>

        <b-table
          striped
          hover
          responsive
          :items="selectedDenounced.complaints"
          :fields="complaintFields"
          table-class="modern-table"
        >
          <template #cell(protocol)="data">
            <b-button
              variant="link"
              class="p-0 text-primary font-weight-bold"
              @click="goToComplaint(data.item._id)"
            >
              {{ data.value }}
            </b-button>
          </template>

          <template #cell(createdAt)="data">
            <span>{{ formatDate(data.value) }}</span>
          </template>

          <template #head(description)>
            <div>
              <div class="description-title">Resumo Anonimizado</div>
              <b-alert variant="warning" show class="mt-1 mb-0">
                <i class="fas fa-exclamation-triangle mr-2"></i>
                Este resumo foi gerado por Inteligência Artificial e não passou
                por validação humana.
              </b-alert>
            </div>
          </template>

          <template #cell(description)="data">
            <div>
              <p
                v-if="data.item.summary || data.item.text"
                class="description-text"
              >
                {{ data.item.summary || data.item.text }}
              </p>
              <p v-else class="description-text text-muted">
                Descrição não disponível
              </p>
            </div>
          </template>
        </b-table>
      </div>
    </b-modal>

    <!-- Modal para adicionar denunciado -->
    <b-modal
      v-model="showAddDenouncedModal"
      title="Vincular Denunciado"
      size="lg"
      centered
      hide-footer
      @hidden="closeAddDenouncedModal"
    >
      <b-form @submit.prevent="handleAddDenouncedSubmit">
        <div class="form-section mb-4">
          <h6 class="section-title">Informações do Denunciado</h6>

          <b-row>
            <b-col md="12">
              <b-form-group label="Protocolo" label-for="protocol">
                <b-form-input
                  id="protocol"
                  v-model="denouncedForm.protocol"
                  required
                  placeholder="Digite o protocolo"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="12">
              <b-form-group label="Nome" label-for="name">
                <div class="position-relative">
                  <b-form-input
                    id="name"
                    v-model="personSearchText"
                    type="text"
                    required
                    placeholder="Digite o nome da pessoa..."
                    autocomplete="off"
                    @input="onPersonSearch"
                    @focus="showPersonSuggestions = true"
                    @blur="hidePersonSuggestions"
                  ></b-form-input>

                  <!-- Dropdown de sugestões -->
                  <div
                    v-if="showPersonSuggestions && filteredPeople.length > 0"
                    class="person-suggestions-dropdown"
                  >
                    <div
                      v-for="person in filteredPeople"
                      :key="person.value"
                      class="suggestion-item"
                      @mousedown="selectPerson(person)"
                    >
                      {{ person.text }}
                    </div>
                  </div>

                  <!-- Mensagem quando não há resultados -->
                  <div
                    v-if="
                      showPersonSuggestions &&
                      filteredPeople.length === 0 &&
                      personSearchText.length > 0
                    "
                    class="person-suggestions-dropdown"
                  >
                    <div class="suggestion-item no-results">
                      Nenhuma pessoa encontrada
                    </div>
                  </div>
                </div>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="4">
              <b-form-group
                label="Tipo de Relacionamento"
                label-for="relationshipType"
              >
                <b-form-input
                  id="relationshipType"
                  v-model="denouncedForm.relationshipType"
                  readonly
                  placeholder="Tipo será preenchido automaticamente"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Cargo" label-for="office">
                <b-form-input
                  id="office"
                  v-model="denouncedForm.office"
                  readonly
                  placeholder="Cargo será preenchido automaticamente"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Departamento/Setor" label-for="department">
                <b-form-input
                  id="department"
                  v-model="denouncedForm.department"
                  readonly
                  placeholder="Departamento será preenchido automaticamente"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row v-if="showCnpjField">
            <b-col md="6">
              <b-form-group label="CPF/CNPJ" label-for="cpfcnpj">
                <b-form-input
                  id="cpfcnpj"
                  v-model="denouncedForm.cpfcnpj"
                  readonly
                  placeholder="CPF/CNPJ será preenchido automaticamente"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Aviso de duplicata -->
          <b-row v-if="duplicateWarning">
            <b-col md="12">
              <b-alert variant="warning" show class="mt-3">
                <i class="fas fa-exclamation-triangle mr-2"></i>
                {{ duplicateWarning }}
              </b-alert>
            </b-col>
          </b-row>
        </div>

        <!-- Botões de ação -->
        <div class="form-actions d-flex justify-content-end">
          <b-button
            type="button"
            variant="secondary"
            :disabled="submitting"
            class="mr-2"
            @click="closeAddDenouncedModal"
          >
            Cancelar
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            :disabled="submitting || duplicateWarning"
          >
            <b-spinner v-if="submitting" small></b-spinner>
            <i v-else class="fas fa-save"></i>
            Vincular Denunciado
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- Modal para adicionar denúncia pelo protocolo -->
    <b-modal
      v-model="showAddComplaintModal"
      title="Vincular Denúncia"
      size="md"
      centered
      hide-footer
      @hidden="closeAddComplaintModal"
    >
      <b-form @submit.prevent="handleAddComplaintSubmit">
        <div class="form-section mb-4">
          <h6 class="section-title">Informações da Denúncia</h6>

          <b-alert variant="info" show class="mb-3">
            <i class="fas fa-info-circle mr-2"></i>
            Digite o número do protocolo da denúncia que deseja vincular ao
            usuário
            <strong>{{ selectedDenounced && selectedDenounced.name }}</strong
            >.
          </b-alert>

          <b-row>
            <b-col md="12">
              <b-form-group
                label="Número do Protocolo"
                label-for="complaintProtocol"
              >
                <b-input-group>
                  <b-form-input
                    id="complaintProtocol"
                    v-model="complaintForm.protocol"
                    required
                    placeholder="Digite o número do protocolo"
                    :disabled="submittingComplaint"
                  ></b-form-input>
                  <b-input-group-append v-if="searchingComplaint">
                    <b-spinner small></b-spinner>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
          </b-row>

          <div
            v-if="complaintForm.complaintInfo"
            class="complaint-preview mt-3"
          >
            <h6 class="text-muted mb-2">Informações da Denúncia</h6>
            <div class="alert alert-light">
              <div class="row">
                <div class="col-md-6">
                  <strong>Protocolo:</strong>
                  {{ complaintForm.complaintInfo.protocol }}
                </div>
                <div class="col-md-6">
                  <strong>Data:</strong>
                  {{ formatDate(complaintForm.complaintInfo.createdAt) }}
                </div>
                <div class="col-md-12 mt-2">
                  <strong>Resumo:</strong>
                  {{
                    complaintForm.complaintInfo.summary ||
                    complaintForm.complaintInfo.text ||
                    "Não disponível"
                  }}
                </div>
              </div>
            </div>
          </div>

          <!-- Aviso de duplicata -->
          <b-row v-if="complaintDuplicateWarning">
            <b-col md="12">
              <b-alert variant="warning" show class="mt-3">
                <i class="fas fa-exclamation-triangle mr-2"></i>
                {{ complaintDuplicateWarning }}
              </b-alert>
            </b-col>
          </b-row>
        </div>

        <!-- Botões de ação -->
        <div class="form-actions d-flex justify-content-end">
          <b-button
            type="button"
            variant="secondary"
            :disabled="submittingComplaint"
            class="mr-2"
            @click="closeAddComplaintModal"
          >
            Cancelar
          </b-button>
          <b-button
            type="submit"
            variant="success"
            :disabled="
              submittingComplaint ||
              !complaintForm.protocol ||
              complaintDuplicateWarning
            "
          >
            <b-spinner v-if="submittingComplaint" small></b-spinner>
            <i v-else class="fas fa-plus-circle"></i>
            Adicionar Denúncia
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { denouncedUsersReport } from "@/api/reports";
import { getStatusText } from "../utils";
import ComplaintStatus from "../../../../../backend/models/ComplaintStatus.ts";
import moment from "moment";
import {
  getCompanyClassifications,
  getCompanyUsers,
  getCompanies,
} from "@/api/company";
import { getInternalEmployees, getThirdParties } from "@/api/relationships";
import {
  findComplaintIdByProtocol,
  addDenounced,
  getComplaint,
  getDenounceds,
} from "@/api/complaint";
import ReportPrintHeader from "@/components/ReportPrintHeader.vue";
import reportPrintMixin from "@/mixins/reportPrint";

export default {
  name: "DenouncedUsersReport",
  components: {
    ReportPrintHeader,
  },
  mixins: [reportPrintMixin],
  data() {
    const statusOpts = [];
    for (const status of Object.keys(ComplaintStatus)) {
      const text = getStatusText(ComplaintStatus[status]);
      statusOpts.push({ value: ComplaintStatus[status], text });
    }

    return {
      totalRows: 0,
      loadingReport: false,
      statusOpts,
      filter: {
        relationshipType: [],
        minComplaints: null,
        receivingMeans: [],
        origin: [],
        reportingAgent: [],
        level: [],
        nature: [],
        responsibleDepartment: [],
        involvedDepartment: [],
        businessUnit: [],
        businessGroup: [],
        relatedProject: [],
        complaintClosure: [],
        committee: {
          admin: null,
          members: [],
        },
        selectedCompany: null,
      },
      companyClassification: {
        receivingMeans: {},
        origin: {},
        reportingAgent: {},
        level: {},
        nature: {},
        responsibleDepartment: {},
        involvedDepartment: {},
        businessUnit: {},
        businessGroup: {},
        relatedProject: {},
        complaintClosure: {},
      },
      currentFilter: {
        relationshipType: [],
        minComplaints: null,
        receivingMeans: [],
        origin: [],
        reportingAgent: [],
        level: [],
        nature: [],
        responsibleDepartment: [],
        involvedDepartment: [],
        businessUnit: [],
        businessGroup: [],
        relatedProject: [],
        complaintClosure: [],
        committee: {
          admin: null,
          members: [],
        },
        selectedCompany: null,
      },
      report: { denouncedUsers: [] },
      fields: [
        { key: "name", label: "Nome", sortable: true },
        { key: "office", label: "Cargo", sortable: true },
        { key: "department", label: "Departamento", sortable: true },
        { key: "complaintCount", label: "Denúncias", sortable: true },
      ],
      companyUsers: {},
      availableCompanies: [],
      collapsedSections: {
        empresa: false,
        relacionamento: false,
        denuncias: false,
        classificacoes: true,
        comite: true,
      },
      showModal: false,
      selectedDenounced: null,
      complaintFields: [
        {
          key: "protocol",
          label: "Protocolo",
          sortable: true,
          thClass: "text-center align-middle",
          tdClass: "text-center align-middle",
        },
        {
          key: "createdAt",
          label: "Data da Denúncia",
          sortable: true,
          thClass: "text-center align-middle",
          tdClass: "text-center align-middle",
        },
        {
          key: "description",
          label: "Resumo Anonimizado",
          sortable: false,
          thClass: "description-header",
          tdClass: "align-middle",
        },
      ],
      showAddDenouncedModal: false,
      denouncedForm: {
        protocol: "",
        type: null,
        selectedPerson: null,
        relationshipType: "",
        office: "",
        department: "",
        cpfcnpj: "",
        cnpj: "",
      },
      submitting: false,
      availablePeople: [],
      internalEmployees: [],
      thirdParties: [],
      personSearchText: "",
      filteredPeople: [],
      showPersonSuggestions: false,
      showAddComplaintModal: false,
      complaintForm: {
        protocol: "",
        complaintInfo: null,
      },
      submittingComplaint: false,
      protocolTimeout: null,
      searchingComplaint: false,
      refreshingComplaints: false,
      existingDenounceds: [],
      duplicateWarning: null,
      complaintDuplicateWarning: null,
    };
  },
  computed: {
    ...mapGetters(["profile", "classificationOptions", "classificationMaps"]),
    isMasterUser() {
      return this.profile.role === "root" || this.isCompanyManager;
    },
    isCompanyManager() {
      return (
        this.profile.role === "company-root" &&
        this.profile.managedCompanies &&
        this.profile.managedCompanies.length > 0
      );
    },
    showCompanyFilter() {
      return this.profile.role === "root" || this.isCompanyManager;
    },
    companyUsersList() {
      const users = [];
      for (const user of Object.values(this.companyUsers)) {
        users.push({ value: user._id, text: `${user.name} ${user.surname}` });
      }
      return users;
    },
    showCnpjField() {
      return (
        this.denouncedForm.type === "third" && this.denouncedForm.selectedPerson
      );
    },
    companyOptions() {
      const options = [{ value: null, text: "Todas as Empresas" }];

      this.availableCompanies.forEach((company) => {
        options.push({
          value: company._id,
          text: company.fantasyName || company.name,
        });
      });

      return options;
    },
    relationshipTypeOpts() {
      return [
        { value: "internal", text: "Funcionário Interno" },
        { value: "third-party", text: "Terceiro" },
      ];
    },
    showCompanyColumn() {
      return this.isMasterUser && !this.currentFilter.selectedCompany;
    },
    activeFields() {
      if (!this.showCompanyColumn) {
        return this.fields;
      }

      return [
        { key: "companyName", label: "Empresa", sortable: true },
        ...this.fields,
      ];
    },
    reportItems() {
      if (this.isMasterUser) {
        if (!Array.isArray(this.report)) return [];

        return this.report.flatMap((item) => {
          const companyName =
            item.company?.fantasyName || item.company?.name || "N/A";

          return (item.denouncedUsers || []).map((denouncedUser) => ({
            ...denouncedUser,
            companyName,
          }));
        });
      }

      return (this.report.denouncedUsers || []).map((denouncedUser) => ({
        ...denouncedUser,
        companyName:
          this.report.company?.fantasyName || this.report.company?.name || "",
      }));
    },
    reportTitle() {
      if (!this.isMasterUser) {
        return `Usuários Denunciados da ${
          this.report.company ? this.report.company.fantasyName : "Empresa"
        }`;
      }

      if (this.currentFilter.selectedCompany) {
        const selectedCompany = this.companyOptions.find(
          (company) => company.value === this.currentFilter.selectedCompany
        );

        return `Usuários Denunciados - ${
          selectedCompany ? selectedCompany.text : "Empresa Selecionada"
        }`;
      }

      return "Usuários Denunciados";
    },
    printTitle() {
      return this.reportTitle;
    },
    totalFilteredCount() {
      return this.reportItems.filter((row) =>
        this.filterResults(row, this.currentFilter)
      ).length;
    },
  },
  watch: {
    "complaintForm.protocol": {
      handler: async function (newProtocol) {
        if (newProtocol && newProtocol.trim().length >= 3) {
          // Usar setTimeout para debounce
          clearTimeout(this.protocolTimeout);
          this.protocolTimeout = setTimeout(async () => {
            try {
              this.searchingComplaint = true;
              // Primeiro, buscar o ID da denúncia pelo protocolo
              const complaintResponse = await findComplaintIdByProtocol(
                newProtocol
              );

              if (complaintResponse.data && complaintResponse.data._id) {
                // Depois, buscar os dados completos da denúncia
                const fullComplaintResponse = await getComplaint(
                  complaintResponse.data._id
                );

                if (fullComplaintResponse.data) {
                  // Garantir que temos os campos necessários
                  const complaintData = fullComplaintResponse.data;
                  this.complaintForm.complaintInfo = {
                    _id: complaintData._id,
                    protocol: complaintData.protocol,
                    createdAt: complaintData.createdAt,
                    summary: complaintData.summary,
                    text: complaintData.text,
                    status: complaintData.status,
                  };

                  // Verificar se o denunciado atual já está nesta denúncia
                  await this.checkComplaintDuplicate();
                } else {
                  this.complaintForm.complaintInfo = null;
                  this.complaintDuplicateWarning = null;
                }
              } else {
                this.complaintForm.complaintInfo = null;
              }
            } catch (error) {
              this.complaintForm.complaintInfo = null;
            } finally {
              this.searchingComplaint = false;
            }
          }, 500);
        } else {
          this.complaintForm.complaintInfo = null;
        }
      },
    },
  },
  async created() {
    await this.loadCompanyPrintLogo(this.profile.company);
    await this.loadCompanies();
    await this.loadData();
  },
  methods: {
    getStatusText,
    formatDate(value) {
      if (!value) {
        return "—";
      }
      return moment(value).format("DD/MM/YYYY HH:mm");
    },
    getStatusClass(status) {
      const statusMap = {
        [ComplaintStatus.NEW]: "status-new",
        [ComplaintStatus.REJECTED]: "status-rejected",
        [ComplaintStatus.IN_PROGRESS]: "status-in-progress",
        [ComplaintStatus.PENDING]: "status-pending",
        [ComplaintStatus.DONE]: "status-done",
        [ComplaintStatus.CONCLUDED]: "status-concluded",
        [ComplaintStatus.REOPENED]: "status-reopened",
        [ComplaintStatus.FORWARDED]: "status-forwarded",
      };
      return statusMap[status] || "status-default";
    },
    getClassificationOpts(key) {
      return [
        { value: null, text: "Sem Classificação" },
        ...this.classificationOptions[key],
      ];
    },
    getUsersNames(ids) {
      if (ids === null || ids === undefined) {
        return "";
      }
      if (ids.length === undefined) {
        ids = [ids];
      }
      let names = "";
      for (let i = 0; i < ids.length; i++) {
        if (i > 0) {
          names += ", ";
        }
        const user = this.companyUsers[ids[i]];
        if (user) {
          names += `${user.name} ${user.surname}`;
        }
      }
      return names;
    },
    clearParameters() {
      this.filter.relationshipType = [];
      this.filter.minComplaints = null;
      this.filter.receivingMeans = [];
      this.filter.origin = [];
      this.filter.reportingAgent = [];
      this.filter.level = [];
      this.filter.nature = [];
      this.filter.responsibleDepartment = [];
      this.filter.involvedDepartment = [];
      this.filter.businessUnit = [];
      this.filter.businessGroup = [];
      this.filter.relatedProject = [];
      this.filter.complaintClosure = [];
      this.filter.committee = {
        admin: null,
        members: [],
      };
      this.filter.selectedCompany = null;

      this.receivingMeans = [];
      this.origin = [];
      this.reportingAgent = [];
      this.level = [];
      this.nature = [];
      this.responsibleDepartment = [];
      this.involvedDepartment = [];
      this.businessUnit = [];
      this.businessGroup = [];
      this.relatedProject = [];
      this.complaintClosure = [];
      this.currentFilter = {
        relationshipType: [],
        minComplaints: null,
        receivingMeans: [],
        origin: [],
        reportingAgent: [],
        level: [],
        nature: [],
        responsibleDepartment: [],
        involvedDepartment: [],
        businessUnit: [],
        businessGroup: [],
        relatedProject: [],
        complaintClosure: [],
        committee: {
          admin: null,
          members: [],
        },
        selectedCompany: null,
      };
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
    },
    onFilterChange() {
      this.currentFilter.relationshipType = this.filter.relationshipType;
      this.currentFilter.minComplaints = this.filter.minComplaints
        ? parseInt(this.filter.minComplaints)
        : null;
      this.currentFilter.receivingMeans = this.filter.receivingMeans;
      this.currentFilter.origin = this.filter.origin;
      this.currentFilter.reportingAgent = this.filter.reportingAgent;
      this.currentFilter.level = this.filter.level;
      this.currentFilter.nature = this.filter.nature;
      this.currentFilter.responsibleDepartment =
        this.filter.responsibleDepartment;
      this.currentFilter.involvedDepartment = this.filter.involvedDepartment;
      this.currentFilter.businessUnit = this.filter.businessUnit;
      this.currentFilter.businessGroup = this.filter.businessGroup;
      this.currentFilter.relatedProject = this.filter.relatedProject;
      this.currentFilter.complaintClosure = this.filter.complaintClosure;
      this.currentFilter.committee = this.filter.committee;
      this.currentFilter.selectedCompany = this.filter.selectedCompany;

      // Se a empresa foi alterada, recarregar os dados
      if (this.isMasterUser) {
        this.loadData(false);
      }
    },
    filterResults(row, filter) {
      // Filtro por tipo de relacionamento
      if (
        filter.relationshipType &&
        filter.relationshipType.length &&
        filter.relationshipType.indexOf(row.relationshipType) === -1
      ) {
        return false;
      }

      // Filtro por número mínimo de denúncias
      if (filter.minComplaints && row.complaintCount < filter.minComplaints) {
        return false;
      }

      if (filter.fromDate || filter.toDate) {
        const created = moment(row.createdAt).toDate();
        if (filter.fromDate && filter.fromDate > created) {
          return false;
        }
        if (filter.toDate && filter.toDate < created) {
          return false;
        }
      }
      if (
        filter.status &&
        filter.status.length &&
        filter.status.indexOf(row.status) === -1
      ) {
        return false;
      }
      const classifications = [
        "receivingMeans",
        "origin",
        "reportingAgent",
        "level",
        "nature",
        "responsibleDepartment",
        "involvedDepartment",
        "businessUnit",
        "businessGroup",
        "complaintClosure",
        "relatedProject",
      ];
      const rowClassification = row.classification || {};
      for (const key of classifications) {
        if (
          filter[key] &&
          filter[key].length &&
          filter[key].indexOf(rowClassification[key]) === -1
        ) {
          return false;
        }
      }
      if (filter.committee.admin && row.committee) {
        if (row.committee.admin !== filter.committee.admin) {
          return false;
        }
      }
      if (
        filter.committee.members &&
        filter.committee.members.length &&
        row.committee &&
        row.committee.users
      ) {
        for (const member of filter.committee.members) {
          if (!row.committee.users.includes(member)) {
            return false;
          }
        }
      }
      return true;
    },

    exportCSV() {
      const classificationKeys = [
        "receivingMeans",
        "origin",
        "reportingAgent",
        "level",
        "nature",
        "responsibleDepartment",
        "involvedDepartment",
        "businessUnit",
        "businessGroup",
        "relatedProject",
        "complaintClosure",
      ];

      const escapeCsv = (value) => {
        if (value === null || value === undefined) return "";
        if (Array.isArray(value)) value = value.join(", ");
        if (typeof value === "object") value = JSON.stringify(value);
        value = String(value);
        const needsQuotes = /[",\n\r;]/.test(value);
        const escaped = value.replace(/"/g, '""');
        return needsQuotes ? `"${escaped}"` : escaped;
      };

      const getCellRawValue = (row, key) => {
        if (key in row) return row[key];
        if (classificationKeys.includes(key)) {
          return row.classification ? row.classification[key] : null;
        }
        return null;
      };

      const formatValue = (field, row) => {
        let value = getCellRawValue(row, field.key);
        if (field.formatter) {
          try {
            return field.formatter(value, field.key, row);
          } catch (e) {
            // fallback below
          }
        }
        switch (field.key) {
          case "status":
            return this.getStatusText(row.status);
          case "committeeAdm":
            return this.getUsersNames(
              row.committee ? row.committee.admin : null
            );
          case "committee":
            return this.getUsersNames(row.committee ? row.committee.users : []);
          case "createdAt":
          case "updatedAt":
            return this.formatDate(value);
          case "relationshipType":
            if (!value) return "-";
            return value === "internal" ? "Funcionário Interno" : "Terceiro";
          default:
            return value == null ? "" : value;
        }
      };

      const headers = this.activeFields.map((f) => f.label || f.key);
      const allRows = this.reportItems
        .filter((row) => this.filterResults(row, this.currentFilter))
        .map((row) => this.activeFields.map((f) => formatValue(f, row)));

      const separator = ";";
      const lines = [headers, ...allRows]
        .map((row) => row.map(escapeCsv).join(separator))
        .join("\r\n");

      const bom = "\ufeff"; // Excel-friendly BOM
      const blob = new Blob([bom + lines], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = moment().format("YYYYMMDD_HHmmss");
      a.href = url;
      a.download = `relatorio_usuarios_denunciados_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },

    updateFields() {
      // Sempre mostrar apenas as colunas solicitadas
      this.fields = [
        { key: "name", label: "Nome", sortable: true },
        {
          key: "relationshipType",
          label: "Tipo de Relacionamento",
          sortable: true,
        },
        { key: "office", label: "Cargo", sortable: true },
        { key: "department", label: "Departamento", sortable: true },
        { key: "complaintCount", label: "Denúncias", sortable: true },
      ];
    },

    toggleSection(section) {
      this.collapsedSections[section] = !this.collapsedSections[section];
    },

    showComplaintsModal(denouncedUser) {
      this.selectedDenounced = denouncedUser;
      this.showModal = true;
      this.refreshingComplaints = false;
    },

    async refreshUserComplaints() {
      if (!this.selectedDenounced) return;

      this.refreshingComplaints = true;
      try {
        // Recarregar apenas o relatório, sem as classificações
        await this.loadData(false);

        // Encontrar o usuário atualizado na lista
        let updatedUser = null;

        if (this.isMasterUser) {
          for (const item of this.report) {
            const foundUser = item.denouncedUsers?.find(
              (user) => user.cpfcnpj === this.selectedDenounced.cpfcnpj
            );
            if (foundUser) {
              updatedUser = foundUser;
              break;
            }
          }
        } else {
          updatedUser = this.report.denouncedUsers?.find(
            (user) => user.cpfcnpj === this.selectedDenounced.cpfcnpj
          );
        }

        // Atualizar o usuário selecionado com os novos dados
        if (updatedUser) {
          this.$set(this, "selectedDenounced", updatedUser);
        }
      } catch (error) {
        console.error("Erro ao atualizar dados do usuário:", error);
      } finally {
        this.refreshingComplaints = false;
      }
    },

    goToComplaint(complaintId) {
      this.$router.push({ path: `/denuncias/${complaintId}` });
    },

    openAddDenouncedModal() {
      this.showAddDenouncedModal = true;
      this.loadAvailablePeople();
    },

    async loadAvailablePeople() {
      try {
        // Carregar funcionários internos
        const internalResponse = await getInternalEmployees();
        this.internalEmployees = internalResponse.data;

        // Carregar terceiros
        const thirdResponse = await getThirdParties();
        this.thirdParties = thirdResponse.data;

        // Criar lista unificada de pessoas
        this.availablePeople = [];

        // Adicionar funcionários internos
        this.internalEmployees.forEach((employee) => {
          this.availablePeople.push({
            value: `employee_${employee._id}`,
            text: employee.name,
            type: "employee",
            data: employee,
          });
        });

        // Adicionar terceiros
        this.thirdParties.forEach((third) => {
          this.availablePeople.push({
            value: `third_${third._id}`,
            text: third.name,
            type: "third",
            data: third,
          });
        });
      } catch (error) {
        this.$bvToast.toast(
          "Não foi possível carregar as pessoas disponíveis.",
          {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          }
        );
      }
    },

    async loadCompanies() {
      if (!this.isMasterUser) return;

      try {
        const response = await getCompanies({ size: 1000, page: 1 });
        this.availableCompanies = response.data.docs || [];
      } catch (error) {
        console.error("Erro ao carregar empresas:", error);
        this.$bvToast.toast("Não foi possível carregar a lista de empresas.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      }
    },

    onPersonSearch() {
      if (this.personSearchText.length === 0) {
        this.filteredPeople = [];
        this.denouncedForm.selectedPerson = null;
        this.clearPersonFields();
        return;
      }

      // Filtrar pessoas baseado no texto digitado
      this.filteredPeople = this.availablePeople.filter((person) =>
        person.text.toLowerCase().includes(this.personSearchText.toLowerCase())
      );
    },

    async selectPerson(person) {
      this.personSearchText = person.text;
      this.denouncedForm.selectedPerson = person.value;
      this.showPersonSuggestions = false;
      await this.onPersonSelect();
    },

    hidePersonSuggestions() {
      // Usar setTimeout para permitir que o clique na sugestão seja processado
      setTimeout(() => {
        this.showPersonSuggestions = false;
      }, 200);
    },

    clearPersonFields() {
      this.denouncedForm.type = null;
      this.denouncedForm.relationshipType = "";
      this.denouncedForm.office = "";
      this.denouncedForm.department = "";
      this.denouncedForm.cpfcnpj = "";
      this.denouncedForm.cnpj = "";
      this.duplicateWarning = null;
    },

    async onPersonSelect() {
      if (!this.denouncedForm.selectedPerson) {
        this.denouncedForm.type = null;
        this.denouncedForm.relationshipType = "";
        this.denouncedForm.office = "";
        this.denouncedForm.department = "";
        this.denouncedForm.cpfcnpj = "";
        this.denouncedForm.cnpj = "";
        this.duplicateWarning = null;
        return;
      }

      // Encontrar a pessoa selecionada na lista unificada
      const selectedPerson = this.availablePeople.find(
        (person) => person.value === this.denouncedForm.selectedPerson
      );

      if (!selectedPerson) {
        return;
      }

      // Determinar o tipo automaticamente
      this.denouncedForm.type = selectedPerson.type;

      if (selectedPerson.type === "employee") {
        const employee = selectedPerson.data;
        this.denouncedForm.relationshipType = "Funcionário Interno";
        this.denouncedForm.office = employee.position || "";
        this.denouncedForm.department = employee.area || "";
        this.denouncedForm.cpfcnpj = employee.cpfcnpj;
        this.denouncedForm.cnpj = "";
      } else if (selectedPerson.type === "third") {
        const third = selectedPerson.data;
        this.denouncedForm.relationshipType = "Terceiro";
        this.denouncedForm.office =
          third.type === "individuo" ? "Pessoa Terceira" : "Empresa Terceira";

        // Para terceiros, mostrar informações mais apropriadas no departamento
        if (third.type === "individuo") {
          // Para pessoas, mostrar "Pessoa Terceira" como departamento
          this.denouncedForm.department = "Pessoa Terceira";
          this.denouncedForm.cnpj = "";
        } else {
          // Para empresas, mostrar "Empresa Terceira" como departamento e o CNPJ
          this.denouncedForm.department = "Empresa Terceira";
          this.denouncedForm.cnpj = third.cpfcnpj || "Não informado";
        }

        this.denouncedForm.cpfcnpj = third.cpfcnpj;
      }

      // Verificar se o denunciado já está relacionado ao protocolo
      await this.checkForDuplicateDenounced();
    },

    async checkForDuplicateDenounced() {
      if (!this.denouncedForm.protocol || !this.denouncedForm.cpfcnpj) {
        this.duplicateWarning = null;
        return;
      }

      try {
        // Buscar a denúncia pelo protocolo
        const complaintResponse = await findComplaintIdByProtocol(
          this.denouncedForm.protocol
        );
        const complaint = complaintResponse.data;

        if (!complaint) {
          this.duplicateWarning = null;
          return;
        }

        // Buscar denunciados existentes da denúncia
        const denouncedsResponse = await getDenounceds(complaint._id);
        const existingDenounceds = denouncedsResponse.data;

        // Verificar se já existe um denunciado com o mesmo CPF/CNPJ
        const isDuplicate = existingDenounceds.some(
          (denounced) => denounced.cpfcnpj === this.denouncedForm.cpfcnpj
        );

        if (isDuplicate) {
          this.duplicateWarning = `Este denunciado (CPF/CNPJ: ${this.denouncedForm.cpfcnpj}) já foi relacionado ao protocolo ${this.denouncedForm.protocol}.`;

          // Notificação imediata
          this.$bvToast.toast(this.duplicateWarning, {
            title: "Atenção",
            toastClass: "custom-toast custom-toast-warning",
            variant: "light",
            solid: true,
            autoHideDelay: 8000,
            noCloseButton: false,
          });
        } else {
          this.duplicateWarning = null;

          // Notificação de sucesso (opcional - pode remover se não quiser)
          this.$bvToast.toast(
            "Denunciado selecionado com sucesso. Pode prosseguir com o salvamento.",
            {
              title: "Sucesso",
              toastClass: "custom-toast custom-toast-success",
              variant: "light",
              solid: true,
              autoHideDelay: 5000,
              noCloseButton: false,
            }
          );
        }
      } catch (error) {
        console.error("Erro ao verificar duplicatas:", error);
        this.duplicateWarning = null;
      }
    },

    async handleAddDenouncedSubmit() {
      if (
        !this.denouncedForm.protocol ||
        !this.denouncedForm.type ||
        !this.denouncedForm.selectedPerson
      ) {
        this.$bvToast.toast(
          "Por favor, preencha todos os campos obrigatórios.",
          {
            title: "Atenção",
            toastClass: "custom-toast custom-toast-warning",
            variant: "light",
            solid: true,
            autoHideDelay: 8000,
            noCloseButton: false,
          }
        );
        return;
      }

      // Verificar se há aviso de duplicata (verificação extra de segurança)
      if (this.duplicateWarning) {
        this.$bvToast.toast(
          "Não é possível salvar: denunciado já relacionado a este protocolo.",
          {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          }
        );
        return;
      }

      this.submitting = true;
      try {
        // Primeiro, buscar a denúncia pelo protocolo
        const complaintResponse = await findComplaintIdByProtocol(
          this.denouncedForm.protocol
        );
        const complaint = complaintResponse.data;

        if (!complaint) {
          this.$bvToast.toast(
            "Denúncia não encontrada com o protocolo informado.",
            {
              title: "Erro",
              toastClass: "custom-toast custom-toast-error",
              variant: "light",
              solid: true,
              autoHideDelay: 10000,
              noCloseButton: false,
            }
          );
          return;
        }

        // Obter o nome da pessoa selecionada
        const selectedPerson = this.availablePeople.find(
          (person) => person.value === this.denouncedForm.selectedPerson
        );
        const personName = selectedPerson ? selectedPerson.text : "";

        // Preparar dados para salvar
        const denouncedData = {
          name: personName,
          cpfcnpj: this.denouncedForm.cpfcnpj,
          office: this.denouncedForm.office,
          department: this.denouncedForm.department,
        };

        // Adicionar CNPJ se for uma empresa terceira
        if (this.denouncedForm.type === "third" && this.denouncedForm.cnpj) {
          denouncedData.cnpj = this.denouncedForm.cnpj;
        }

        // Adicionar o denunciado à denúncia usando a API existente
        await addDenounced(complaint._id, denouncedData);

        this.closeAddDenouncedModal();

        // Notificar sucesso
        this.$bvToast.toast("Denunciado adicionado com sucesso!", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });

        // Recarregar o relatório imediatamente (apenas dados essenciais)
        await this.loadData(false);

        // Se o modal de denúncias estiver aberto, atualizar os dados do usuário
        if (this.showModal && this.selectedDenounced) {
          await this.refreshUserComplaints();
        }
      } catch (error) {
        this.$bvToast.toast(
          error.response?.data?.message ||
            "Não foi possível vincular o denunciado.",
          {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          }
        );
      } finally {
        this.submitting = false;
      }
    },

    closeAddDenouncedModal() {
      this.showAddDenouncedModal = false;
      this.denouncedForm = {
        protocol: "",
        type: null,
        selectedPerson: null,
        relationshipType: "",
        office: "",
        department: "",
        cpfcnpj: "",
        cnpj: "",
      };
      this.availablePeople = [];
      this.personSearchText = "";
      this.filteredPeople = [];
      this.showPersonSuggestions = false;
    },

    openAddComplaintModal() {
      this.showAddComplaintModal = true;
      this.complaintForm = {
        protocol: "",
        complaintInfo: null,
      };
      this.complaintDuplicateWarning = null;
    },

    async checkComplaintDuplicate() {
      if (!this.complaintForm.complaintInfo || !this.selectedDenounced) {
        this.complaintDuplicateWarning = null;
        return;
      }

      try {
        // Buscar os denunciados da denúncia usando o endpoint específico
        const denouncedsResponse = await getDenounceds(
          this.complaintForm.complaintInfo._id
        );
        const existingDenounced = denouncedsResponse.data || [];

        const isAlreadyDenounced = existingDenounced.some(
          (denounced) => denounced.cpfcnpj === this.selectedDenounced.cpfcnpj
        );

        if (isAlreadyDenounced) {
          this.complaintDuplicateWarning = `O usuário ${this.selectedDenounced.name} (CPF/CNPJ: ${this.selectedDenounced.cpfcnpj}) já está denunciado no protocolo ${this.complaintForm.protocol}.`;

          // Notificação imediata
          this.$bvToast.toast(this.complaintDuplicateWarning, {
            title: "Atenção",
            toastClass: "custom-toast custom-toast-warning",
            variant: "light",
            solid: true,
            autoHideDelay: 8000,
            noCloseButton: false,
          });
        } else {
          this.complaintDuplicateWarning = null;

          // Notificação de sucesso
          this.$bvToast.toast(
            `Protocolo ${this.complaintForm.protocol} encontrado. Pode prosseguir com a adição.`,
            {
              title: "Sucesso",
              toastClass: "custom-toast custom-toast-success",
              variant: "light",
              solid: true,
              autoHideDelay: 5000,
              noCloseButton: false,
            }
          );
        }
      } catch (error) {
        console.error("Erro ao verificar duplicatas da denúncia:", error);
        this.complaintDuplicateWarning = null;
      }
    },

    async handleAddComplaintSubmit() {
      if (!this.complaintForm.protocol) {
        this.$bvToast.toast("Por favor, digite o número do protocolo.", {
          title: "Atenção",
          toastClass: "custom-toast custom-toast-warning",
          variant: "light",
          solid: true,
          autoHideDelay: 8000,
          noCloseButton: false,
        });
        return;
      }

      // Verificar se há aviso de duplicata (verificação extra de segurança)
      if (this.complaintDuplicateWarning) {
        this.$bvToast.toast(
          "Não é possível vincular: usuário já está vinculado a este protocolo.",
          {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          }
        );
        return;
      }

      this.submittingComplaint = true;
      try {
        // Primeiro, buscar o ID da denúncia pelo protocolo
        const complaintResponse = await findComplaintIdByProtocol(
          this.complaintForm.protocol
        );

        if (!complaintResponse.data || !complaintResponse.data._id) {
          this.$bvToast.toast(
            "Denúncia não encontrada com o protocolo informado.",
            {
              title: "Erro",
              toastClass: "custom-toast custom-toast-error",
              variant: "light",
              solid: true,
              autoHideDelay: 10000,
              noCloseButton: false,
            }
          );
          return;
        }

        // Depois, buscar os dados completos da denúncia
        const fullComplaintResponse = await getComplaint(
          complaintResponse.data._id
        );
        const complaint = fullComplaintResponse.data;

        if (!complaint) {
          this.$bvToast.toast(
            "Não foi possível obter os dados completos da denúncia.",
            {
              title: "Erro",
              toastClass: "custom-toast custom-toast-error",
              variant: "light",
              solid: true,
              autoHideDelay: 10000,
              noCloseButton: false,
            }
          );
          return;
        }

        // Verificar se a denúncia já tem o denunciado atual
        const existingDenounced = complaint.denounced || [];
        const isAlreadyDenounced = existingDenounced.some(
          (denounced) => denounced.cpfcnpj === this.selectedDenounced.cpfcnpj
        );

        if (isAlreadyDenounced) {
          this.$bvToast.toast(
            "Este usuário já está denunciado nesta denúncia.",
            {
              title: "Atenção",
              toastClass: "custom-toast custom-toast-warning",
              variant: "light",
              solid: true,
              autoHideDelay: 8000,
              noCloseButton: false,
            }
          );
          return;
        }

        // Preparar dados do denunciado
        const denouncedData = {
          name: this.selectedDenounced.name,
          cpfcnpj: this.selectedDenounced.cpfcnpj,
          office: this.selectedDenounced.office,
          department: this.selectedDenounced.department,
        };

        // Adicionar o denunciado à denúncia
        await addDenounced(complaint._id, denouncedData);

        this.closeAddComplaintModal();

        // Notificar sucesso
        this.$bvToast.toast("Denúncia adicionada com sucesso!", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });

        // Recarregar apenas os dados do usuário atual no modal
        await this.refreshUserComplaints();
      } catch (error) {
        this.$bvToast.toast(
          error.response?.data?.message ||
            "Não foi possível adicionar a denúncia.",
          {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          }
        );
      } finally {
        this.submittingComplaint = false;
      }
    },

    closeAddComplaintModal() {
      this.showAddComplaintModal = false;
      this.complaintForm = {
        protocol: "",
        complaintInfo: null,
      };
      this.searchingComplaint = false;
      this.complaintDuplicateWarning = null;
      // Limpar timeout se existir
      if (this.protocolTimeout) {
        clearTimeout(this.protocolTimeout);
        this.protocolTimeout = null;
      }
    },

    async loadData(loadFullData = true) {
      this.loadingReport = true;

      try {
        if (this.isMasterUser) {
          let response;
          try {
            response = await denouncedUsersReport(null);
          } catch (error) {
            if (error.response.status === 403) {
              this.$bvToast.toast(error.response.data, {
                title: "Erro",
                toastClass: "custom-toast custom-toast-error",
                variant: "light",
                solid: true,
                autoHideDelay: 10000,
                noCloseButton: false,
              });
            } else {
              this.$bvToast.toast(error.response.statusText, {
                title: "Erro",
                toastClass: "custom-toast custom-toast-error",
                variant: "light",
                solid: true,
                autoHideDelay: 10000,
                noCloseButton: false,
              });
            }
            return;
          }
          // Para usuários root, o backend retorna um array com dados de todas as empresas
          const allData = response.data || [];

          // Se uma empresa específica foi selecionada, filtrar apenas aquela empresa
          if (this.filter.selectedCompany) {
            const filteredData = allData.filter(
              (item) =>
                item.company && item.company._id === this.filter.selectedCompany
            );
            this.report = filteredData.length > 0 ? filteredData : [];
          } else {
            // Mostrar todas as empresas
            this.report = allData;
          }

          // Forçar reatividade dos dados
          this.$set(this, "report", this.report);
        } else {
          // Se loadFullData for false, apenas carregar o relatório sem recarregar classificações
          if (loadFullData) {
            await this.$store.dispatch(
              "RefreshCompanyClassifications",
              this.profile.company
            );
            let response;
            try {
              response = await getCompanyClassifications(this.profile.company);
              this.companyClassification = response.data;
              response = await getCompanyUsers(this.profile.company, true);
              this.companyUsers = response.data.reduce((obj, user) => {
                obj[user["_id"]] = user;
                return obj;
              }, {});
            } catch (error) {
              console.error("Erro ao carregar classificações:", error);
            }
          }

          // Sempre carregar o relatório
          let response;
          try {
            response = await denouncedUsersReport(this.profile.company);
            this.report = response.data || {
              denouncedUsers: [],
              company: null,
            };
            this.totalRows = this.report.denouncedUsers.length;
            // Forçar reatividade dos dados
            this.$set(this, "report", this.report);
          } catch (error) {
            if (error.response.status === 403) {
              this.$bvToast.toast(error.response.data, {
                title: "Erro",
                toastClass: "custom-toast custom-toast-error",
                variant: "light",
                solid: true,
                autoHideDelay: 10000,
                noCloseButton: false,
              });
            } else {
              this.$bvToast.toast(error.response.statusText, {
                title: "Erro",
                toastClass: "custom-toast custom-toast-error",
                variant: "light",
                solid: true,
                autoHideDelay: 10000,
                noCloseButton: false,
              });
            }
            return;
          }
        }
      } finally {
        this.loadingReport = false;
      }
    },
  },
};
</script>

<style scoped>
.denounced-users-report {
  height: 100vh;
  overflow: hidden;
}

.denounced-users-report .row {
  height: 100%;
  margin: 0;
  align-items: stretch;
}

.denounced-users-report .col-3,
.denounced-users-report .col-9 {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.denounced-users-report .col-9 {
  padding-right: 15px;
}

.denounced-users-report .col-3 {
  padding-left: 15px;
}
.filters-sidebar {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
  border: none;
  border-radius: 8px;
}
/* Harmonize the report card style with the parameters card */
.denounced-users-report #report.card {
  border: none;
  border-radius: 8px;
}
.filters-sidebar >>> .card-header {
  background-color: transparent;
  border-bottom: 1px solid #e9ecef;
  font-weight: 600;
  color: #343a40;
  font-size: 1.1rem;
}
.filters-sidebar .card-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.filters-container {
  flex: 1;
  overflow-y: auto;
  padding-right: 10px;
}

.denounced-users-report .col-9 .card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.denounced-users-report .col-9 .card-body {
  flex: 1;
  overflow-y: auto;
}

.skeleton-overlay {
  position: relative;
}

.skeleton-table {
  border-spacing: 0;
}

.skeleton-table tbody tr {
  box-shadow: none;
}

.skeleton-table tbody td {
  background-color: #fff;
}

.filter-group {
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.filter-group:last-child {
  border-bottom: none;
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 0;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.filter-header:hover {
  background-color: transparent;
}

.filter-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 0.75rem;
  padding-bottom: 0;
  border-bottom: none;
  flex: 1;
  font-size: 0.9rem;
}

.collapse-btn {
  color: #6c757d;
  transition: color 0.2s ease, transform 0.2s ease;
  line-height: 1;
}

.collapse-btn:hover {
  color: #007bff;
  text-decoration: none;
}

.collapse-btn i {
  transition: transform 0.3s ease;
  font-size: 0.875rem;
}

.filter-header:hover .collapse-btn {
  color: #007bff;
}

/* Animação suave para o collapse */
.collapse {
  transition: all 0.3s ease;
}

.collapsing {
  transition: height 0.3s ease;
}

.form-control[multiple] {
  min-height: 80px;
}

/* Estilos para checkboxes e radio buttons */
.form-check {
  padding-left: 1.5rem;
  margin-bottom: 0.5rem;
}

.form-check-input {
  margin-left: -1.5rem;
}

.form-check-label {
  font-size: 0.875rem;
  line-height: 1.4;
  color: #495057;
  cursor: pointer;
  transition: color 0.2s ease;
}

.form-check-label:hover {
  color: #007bff;
}

.custom-checkbox .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #007bff;
  border-color: #007bff;
}

.custom-radio .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #007bff;
  border-color: #007bff;
}

/* Espaçamento entre grupos de checkboxes */
.filter-group .form-group:not(:last-child) {
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f0f0f0;
}

/* Scroll suave para containers de checkboxes */
.filter-group {
  max-height: none;
}

.filter-group .form-check-input:focus {
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.mb-3 {
  margin-bottom: 1rem;
}

.h-100 {
  min-height: calc(100vh - 120px);
}

/* Customização dos scrollbars */
.filters-container::-webkit-scrollbar {
  width: 6px;
}

.filters-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.filters-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.filters-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.d-flex.flex-column.mt-3 .b-button {
  border-radius: 6px;
  padding: 0.6rem 1rem;
  font-weight: 500;
  transition: background-color 0.2s ease, transform 0.2s ease,
    box-shadow 0.2s ease;
}
.d-flex.flex-column.mt-3 .b-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
.d-flex.flex-column.mt-3 .btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}
.d-flex.flex-column.mt-3 .btn-primary {
  background-color: #007bff;
  border-color: #007bff;
}
/* Responsividade */
@media (max-width: 1200px) {
  .denounced-users-report {
    height: auto;
    min-height: 100vh;
  }

  .filters-sidebar {
    height: auto;
    min-height: 400px;
  }

  .filters-container {
    height: auto;
    max-height: 400px;
  }

  .denounced-users-report .col-3,
  .denounced-users-report .col-9 {
    height: auto;
  }
}

@media (max-width: 768px) {
  .denounced-users-report .col-3 {
    flex: 0 0 100%;
    max-width: 100%;
    margin-bottom: 20px;
    height: auto;
    padding-left: 0;
  }

  .denounced-users-report .col-9 {
    flex: 0 0 100%;
    max-width: 100%;
    height: auto;
    padding-right: 0;
  }

  .denounced-users-report .row {
    height: auto;
    flex-direction: column;
  }
}

/* Estilos para o dropdown de sugestões de pessoas */
.person-suggestions-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ced4da;
  border-top: none;
  border-radius: 0 0 0.375rem 0.375rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
}

.suggestion-item {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid #f8f9fa;
  transition: background-color 0.15s ease-in-out;
}

.suggestion-item:hover {
  background-color: #f8f9fa;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.suggestion-item.no-results {
  color: #6c757d;
  font-style: italic;
  cursor: default;
}

.suggestion-item.no-results:hover {
  background-color: transparent;
}
</style>
<style>
.modern-table {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
}

.modern-table thead th {
  background-color: #eef1f4;
  border: none;
  color: #56606b;
  font-weight: 600;
  padding: 1rem;
  text-transform: uppercase;
  font-size: 0.8rem;
  letter-spacing: 0.5px;
}

.modern-table thead th:first-child {
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
}

.modern-table thead th:last-child {
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
}

.modern-table tbody tr {
  background-color: #fff;
}

.modern-table tbody tr:hover {
  background-color: #fbfcfe;
}

.modern-table tbody td {
  border-top: none;
  border-bottom: 1px solid #e6eaef;
  padding: 1rem;
  vertical-align: middle;
}

.modern-table tbody tr:hover td {
  background-color: #fbfcfe;
}
.status-badge {
  padding: 0.4em 0.8em;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #212529;
  white-space: nowrap;
}
.status-new {
  background-color: #fce4e4;
  color: #c0392b;
}
.status-in-progress {
  background-color: #e0e7ff;
  color: #3730a3;
}
.status-done {
  background-color: #d1fae5;
  color: #065f46;
}
.status-concluded {
  background-color: #d1fae5;
  color: #065f46;
}
.status-rejected {
  background-color: #e5e7eb;
  color: #4b5563;
}
.status-pending,
.status-forwarded,
.status-reopened,
.status-default {
  background-color: #e0e7ff;
  color: #3730a3;
}

/* Estilo simples e acessível para o número de denúncias */
.complaint-count-simple {
  text-align: center;
  font-weight: 600;
  font-size: 1rem;
  color: #495057;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 4px 8px;
  display: inline-block;
  min-width: 40px;
  margin: 0 auto;
  cursor: pointer;
  transition: all 0.2s ease;
}

.complaint-count-simple.clickable:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
  transform: scale(1.05);
}

/* Centralizar todas as colunas do relatório */
.modern-table td {
  text-align: center;
}

.modern-table th {
  text-align: center;
}

/* Estilos para o modal de denúncias */
.denounced-info {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.description-text {
  white-space: pre-wrap;
  font-size: 0.95rem;
  line-height: 1.6;
  color: #555;
  margin-bottom: 0;
}

/* Estilos para o cabeçalho da coluna de descrição */
.description-header {
  min-width: 300px;
}

.description-title {
  font-weight: 600;
  color: #6c757d;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
}

.description-header .alert {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
  margin: 0;
  border: none;
}

/* Estilos para o link do protocolo */
.btn-link.text-primary {
  text-decoration: none;
  transition: all 0.2s ease;
}

.btn-link.text-primary:hover {
  text-decoration: underline;
  transform: scale(1.05);
}

/* Centralização vertical das células da tabela */
.modern-table td.align-middle {
  vertical-align: middle !important;
}

.modern-table td {
  vertical-align: middle;
}

/* Centralização vertical dos cabeçalhos da tabela */
.modern-table th.align-middle {
  vertical-align: middle !important;
}

.modern-table th {
  vertical-align: middle;
}

/* Estilos para o modal de adicionar denunciado */
.add-denounced-btn {
  border-radius: 6px;
  padding: 0.6rem 1rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.add-denounced-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Estilos para o botão de adicionar denúncia */
.add-complaint-btn {
  border-radius: 6px;
  padding: 0.6rem 1rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.add-complaint-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Estilos para o preview da denúncia */
.complaint-preview .alert-light {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
}

.complaint-preview .alert-light strong {
  color: #495057;
}

.form-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  border: 1px solid #e9ecef;
}

.section-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 1rem;
  font-size: 1rem;
}

.form-actions {
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

/* Estilização dos campos do formulário */
.form-group label {
  font-weight: 500;
  color: #495057;
  margin-bottom: 0.5rem;
}

.form-control,
.form-select {
  border-radius: 6px;
  border: 1px solid #ced4da;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus,
.form-select:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.form-control[readonly] {
  background-color: #e9ecef;
  color: #6c757d;
}
</style>
